import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://asc-fechten.lovable.app';

const toAbsoluteUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
  ogType?: 'website' | 'article';
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const SEOHead = ({
  title = 'ASC Fechten Wien - Akademischer Sportclub',
  description = 'Der Akademische Sportclub Fechten Wien - Tradition trifft Moderne. Professionelles Fechttraining für alle Altersgruppen seit 1970.',
  keywords = 'Fechten, Wien, Sportverein, Training, Turniere, ASC, Akademischer Sportclub',
  ogImage = '/lovable-uploads/ea6c2094-bd02-4d67-8d6b-4895033aa4fb.png',
  canonical,
  noindex = false,
  ogType = 'website',
  structuredData,
}: SEOHeadProps) => {
  const location = useLocation();
  const currentUrl = `${SITE_URL}${location.pathname}`;
  const canonicalUrl = toAbsoluteUrl(canonical || currentUrl);
  const absoluteOgImage = toAbsoluteUrl(ogImage);
  const serializedStructuredData = structuredData ? JSON.stringify(structuredData) : null;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'ASC Fechten Wien');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', absoluteOgImage, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:site_name', 'ASC Fechten Wien', true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', absoluteOgImage);

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // JSON-LD Structured Data — page specific when provided, sitewide club schema otherwise
    const jsonLd = serializedStructuredData
      ? serializedStructuredData
      : JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SportsClub',
          name: 'ASC Fechten Wien',
          description,
          url: currentUrl,
          logo: absoluteOgImage,
          sameAs: [],
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Wien',
            addressCountry: 'AT',
          },
          sport: 'Fechten',
          foundingDate: '1970',
        });

    let structuredDataScript = document.querySelector(
      'script[type="application/ld+json"][data-seo-head="true"]'
    ) as HTMLScriptElement;
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.setAttribute('data-seo-head', 'true');
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = jsonLd;
  }, [
    title,
    description,
    keywords,
    absoluteOgImage,
    currentUrl,
    canonicalUrl,
    noindex,
    ogType,
    serializedStructuredData,
  ]);

  return null;
};

export default SEOHead;
